import styles from "./TestResults.module.css";
import { DateTime } from "luxon";
import { downloadCertificate } from "@/api/williams/certificates";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FieldPath, get, useFormContext } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getTestDate } from "../lookup/utils";
import { produce } from "immer";
import { RecordStandardGroup } from "@/api/record";
import { SegmentSection } from "./TestResultsSegment";
import { TemplateResults, TestEventResults, TestResult, WelderResults } from "@/api/williams/testEvents";
import { useState } from "react";

type TemplateSectionProps = {
  welder: WelderResults;
  template: TemplateResults;
  path: FieldPath<TestEventResults>;
  isLocked: boolean;
};

export function TemplateSection({ welder, template, path, isLocked }: TemplateSectionProps) {
  const [expanded, setExpanded] = useState<boolean>(false);

  const form = useFormContext<TestEventResults>();
  const error = get(form.formState.errors, path);
  const testDate = getTestDate(template);

  function updateForm(updatedTemplate: TemplateResults) {
    form.setValue(path, updatedTemplate, {
      shouldDirty: true,
    });
  }

  function handleDateChanged(e: React.ChangeEvent<HTMLInputElement>) {
    updateForm(
      produce(template, (draft) => {
        draft.segments.forEach((segment) => {
          segment.testDate = e.target.value;
        });
      })
    );
  }

  function handleResultChanged(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = parseInt(e.target.value) as TestResult;
    const result = value === TestResult.Fail ? TestResult.NotConducted : value;
    updateForm(
      produce(template, (draft) => {
        draft.result = value;
        draft.segments.forEach((segment) => {
          segment.result = result;
          segment.tests.forEach((test) => {
            test.result = result;
          });
        });
      })
    );

    if (value === TestResult.Fail) {
      setExpanded(true);
    }
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mt-2">
        <div className="pe-2">
          <a
            title="Click to download"
            className="link-primary d-inline text-break"
            onClick={() => downloadCertificate(template.recordId)}
          >
            {template.templateNumber}
          </a>

          {template.segments.length > 0 && (
            <FontAwesomeIcon
              role="button"
              icon={expanded ? faChevronUp : faChevronDown}
              size="sm"
              className="ms-1"
              title="Show/Hide Tests"
              onClick={() => setExpanded((prev) => !prev)}
              fixedWidth={true}
            />
          )}
        </div>
        <div className="d-flex align-items-start gap-2">
          <div className={styles.field}>
            <input
              type="date"
              min="1753-01-01"
              max={DateTime.now().toISODate()}
              className={`form-control ${error?.testDate ? "border-danger" : ""}`}
              value={testDate ?? ""}
              onChange={handleDateChanged}
              disabled={template.submitted || template.standard === RecordStandardGroup.WAPI || isLocked}
            />
          </div>
          <div className={styles.field}>
            <select
              value={template.result}
              disabled={template.submitted || isLocked}
              className={`form-select ${error?.result ? "border-danger" : ""}`}
              onChange={handleResultChanged}
            >
              <option value={TestResult.None}></option>
              <option value={TestResult.Pass}>Acceptable</option>
              <option value={TestResult.Fail}>Failed</option>
            </select>
          </div>
        </div>
      </div>

      {expanded &&
        template.segments.map((segment, index) => (
          <SegmentSection
            key={segment.segmentId}
            welder={welder}
            segment={segment}
            template={template}
            isLocked={isLocked}
            path={`${path}.segments.${index}` as FieldPath<TestEventResults>}
          />
        ))}
    </>
  );
}
